import React, { PureComponent } from 'react'
import { observer } from 'mobx-react'
import { action, makeObservable } from 'mobx'
import * as R from 'ramda'
import ModalHeader from '../../../UniformComponents/ModalHeader'
import ModalFooter from '../../../UniformComponents/ModalFooter'
import Button from '../../../UniformComponents/Button'
import Tabs from './Tabs'
import StagedForm from './StagedForm'
import Spinner from '../../../UniformComponents/Spinner'

@observer
class EventSetup extends PureComponent {
    fields = {}

    constructor(props) {
        super(props)
        makeObservable(this)
        const { dashboardWizardStore } = props
        dashboardWizardStore.setSurveyTypes()
        this.setDefaults()
    }

    controls = () => [
        {
            name: 'industry',
            type: 'select',
            label: 'Select your industry',
            options: (store) => Object.values(R.pathOr({}, ['productMap'], store)),
            onChange: (v) => { this.onChange(v, 'industry') },
            show: () => this.showSelect(['productMap']),
            resetNewWidgetOnChange: ['respondentType', 'surveyType', 'productType', 'question', 'displayAs', 'questionId', 'isNumeric', 'questionTypeCode'],
            resetStore: ['questions', 'detailedQuestions'],
            isMandatory: true,
        },
        {
            name: 'productType',
            type: 'select',
            label: 'Select your event type',
            options: (store) => {
                const { dashStore } = store
                const { oId, productType } = dashStore
                if (oId === 6) {
                    return Object.values(R.pathOr({}, ['productMap', store.newWidget.industry, 'children'], store))
                        .filter((o) => o.value === productType)
                }
                return Object.values(R.pathOr({}, ['productMap', store.newWidget.industry, 'children'], store))
            },
            onChange: (v) => { this.onChange(v, 'productType') },
            show: (store) => {
                const { dashStore } = store
                const { oId, productType } = dashStore
                if (oId === 6) {
                    return this.showSelect(Object.values(R.pathOr({}, ['productMap', store.newWidget.industry, 'children'], store))
                        .filter((o) => o.value === productType), true)
                }
                return this.showSelect([
                    'productMap',
                    store.newWidget.industry,
                    'children',
                ])
            },
            isMandatory: true,
        },
        {
            name: 'surveyType',
            type: 'select',
            label: 'Select your survey type',
            options: (store) => {
                const { surveyTypes, dashStore } = store
                const { oId } = dashStore
                const types = Object.values(R.pathOr(
                    {},
                    ['productMap', store.newWidget.industry, 'children', store.newWidget.productType, 'children'],
                    store,
                ))
                if (oId === 6) return types.filter((t) => surveyTypes.includes(t.value))
                return types
            },
            show: (store) => {
                const { surveyTypes, dashStore } = store
                const { oId } = dashStore
                const types = Object.values(R.pathOr(
                    {},
                    ['productMap', store.newWidget.industry, 'children', store.newWidget.productType, 'children'],
                    store,
                ))
                if (oId === 6) return this.showSelect(types.filter((t) => surveyTypes.includes(t.value)), true)
                return this.showSelect(types, true)
            },
            onChange: (v) => { this.onChange(v, 'surveyType') },
            isMandatory: true,
        },
        {
            name: 'respondentType',
            type: 'select',
            label: 'Select your respondent type',
            options: (store) => Object.values(R.pathOr(
                {},
                [
                    'productMap',
                    store.newWidget.industry,
                    'children',
                    store.newWidget.productType,
                    'children',
                    store.newWidget.surveyType,
                    'children',
                ],
                store,
            )),
            show: (store) => this.showSelect([
                'productMap',
                store.newWidget.industry,
                'children',
                store.newWidget.productType,
                'children',
                store.newWidget.surveyType,
                'children',
            ]),
            onChange: (v) => { this.onChange(v, 'respondentType') },
            isMandatory: true,
        },
    ]

    register = (name, validate) => {
        this.fields[name] = validate
    }

    unregister = (name) => {
        Reflect.deleteProperty(this.fields, name)
    }

    @action
    setDefaults = () => {
        const { dashboardWizardStore } = this.props
        const { changeNewWidget, getQuestions } = dashboardWizardStore
        this.controls().forEach((c) => {
            const options = c.options(dashboardWizardStore)
            if (options.length === 1) {
                changeNewWidget(options[0].value, c.name)
                if (c.name === 'respondentType') getQuestions()
            }
        })
    }

    onChange = async ({ value }, field) => {
        const { dashboardWizardStore } = this.props
        const { changeNewWidget, getQuestions, newWidget } = dashboardWizardStore
        changeNewWidget(value, field)
        this.setDefaults()
        if (newWidget.respondentType && newWidget.productType) {
            await getQuestions()
        }
    }

    showSelect = (options, fullOptionsProvided = false) => {
        const { dashboardWizardStore } = this.props
        let storeOptions = fullOptionsProvided
            ? options
            : R.pathOr([], options, dashboardWizardStore)
        if (typeof storeOptions === 'object') storeOptions = Object.values(storeOptions)
        if (storeOptions.length > 1) {
            return 2
        }
        if (storeOptions.length === 1) {
            return 1
        }
        return 1
    }

    complete = () => {
        const { dashboardWizardStore } = this.props
        const { newWidget, nextStage } = dashboardWizardStore
        const valid = Object.keys(this.fields).reduce((isValid, f) => {
            const currentValid = this.fields[f]({ value: newWidget[f], name: f }, true)
            if (!isValid || !currentValid) return false
            return true
        }, true)
        if (valid) nextStage()
    }

    render() {
        const { dashboardWizardStore } = this.props
        const { previousStage, loading } = dashboardWizardStore
        if (loading) return <Spinner />
        return (
            <div className="eventSetup">
                <Tabs dashboardWizardStore={dashboardWizardStore} />
                <ModalHeader cssClass="header">
                    <div className="title">
                        Event Setup
                    </div>
                    <div className="subTitle">
                        Enter your event requirements below
                    </div>
                </ModalHeader>
                <div className="body">
                    <StagedForm
                        store={dashboardWizardStore}
                        controls={this.controls()}
                        valueHolder={dashboardWizardStore.newWidget}
                        register={this.register}
                        unregister={this.unregister}
                    />
                </div>
                <ModalFooter>
                    <Button
                        label="Back"
                        cssClass="tertiary regular"
                        style={{ marginRight: '25px', width: '134px' }}
                        onClick={previousStage}
                    />
                    <Button
                        label="Continue"
                        cssClass="primary regular"
                        style={{ width: '134px' }}
                        onClick={this.complete}
                    />
                </ModalFooter>
            </div>
        )
    }
}

export default EventSetup
